import {
  useState, useEffect 
} from 'react';
import {
  TrendingDown, Clock, Users 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { SourcingEvent } from '@/types/sourcing';

interface BidHistoryEntry {
  rank: number;
  supplierName: string;
  amount: number;
  timestamp: string;
  isYou: boolean;
}

function getMockBidHistory(event: SourcingEvent): BidHistoryEntry[] {
  if (event.eventType !== 'reverse_auction') return [];

  const baseAmount = event.currentLowestBid ?? event.startingPrice ?? 50000;

  return [
    {
      rank: 1,
      supplierName: 'Fresh Foods Co.',
      amount: baseAmount,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      isYou: false,
    },
    {
      rank: 2,
      supplierName: 'You',
      amount: baseAmount * 1.02,
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      isYou: true,
    },
    {
      rank: 3,
      supplierName: 'Quality Produce Inc.',
      amount: baseAmount * 1.05,
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      isYou: false,
    },
    {
      rank: 4,
      supplierName: 'Ocean Fresh Supply',
      amount: baseAmount * 1.08,
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      isYou: false,
    },
    {
      rank: 5,
      supplierName: 'Farm Direct LLC',
      amount: baseAmount * 1.12,
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      isYou: false,
    },
  ];
}

interface AuctionStatusWidgetProps {
  readonly event: SourcingEvent;
  readonly yourBidTotal: number;
  readonly formatCurrency: (amount: number) => string;
}

export function AuctionStatusWidget({
  event,
  yourBidTotal,
  formatCurrency,
}: AuctionStatusWidgetProps): React.JSX.Element {
  const [timeRemaining, setTimeRemaining] = useState('');
  const bidHistory = getMockBidHistory(event);

  useEffect(() => {
    if (!event.auctionEndTime) return;

    const updateTimer = (): void => {
      const end = new Date(event.auctionEndTime ?? '').getTime();
      const now = Date.now();
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining('Auction ended');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        setTimeRemaining(`${days}d ${hours % 24}h remaining`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else {
        setTimeRemaining(`${minutes}m ${seconds}s remaining`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [event.auctionEndTime]);

  const currentLowest = event.currentLowestBid ?? event.startingPrice ?? 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="text-base font-medium">Auction Status</span>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{timeRemaining}</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <CurrentLowestSection
          currentLowest={currentLowest}
          totalBids={event.totalBids}
          formatCurrency={formatCurrency}
        />
        <YourBidSection
          yourBidTotal={yourBidTotal}
          currentLowest={currentLowest}
          formatCurrency={formatCurrency}
        />
        <BidLeaderboard bidHistory={bidHistory} formatCurrency={formatCurrency} />
      </CardContent>
    </Card>
  );
}

interface CurrentLowestSectionProps {
  readonly currentLowest: number;
  readonly totalBids?: number;
  readonly formatCurrency: (amount: number) => string;
}

function CurrentLowestSection({
  currentLowest,
  totalBids,
  formatCurrency,
}: CurrentLowestSectionProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between py-3 border-b">
      <div>
        <p className="text-sm text-muted-foreground">Current Lowest Bid</p>
        <p className="text-2xl font-semibold">{formatCurrency(currentLowest)}</p>
      </div>
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>{totalBids ?? 0} bids</span>
      </div>
    </div>
  );
}

interface YourBidSectionProps {
  readonly yourBidTotal: number;
  readonly currentLowest: number;
  readonly formatCurrency: (amount: number) => string;
}

function YourBidSection({
  yourBidTotal,
  currentLowest,
  formatCurrency,
}: YourBidSectionProps): React.JSX.Element | null {
  if (yourBidTotal <= 0) return null;

  const isLowest = yourBidTotal < currentLowest;
  const difference = Math.abs(yourBidTotal - currentLowest);

  return (
    <div className="flex items-center justify-between py-3 border-b">
      <div>
        <p className="text-sm text-muted-foreground">Your New Bid</p>
        <p className="text-xl font-semibold">{formatCurrency(yourBidTotal)}</p>
      </div>
      <span className={`text-sm ${isLowest ? 'text-green-600' : 'text-muted-foreground'}`}>
        {isLowest ? `${formatCurrency(difference)} below` : `${formatCurrency(difference)} above`}
      </span>
    </div>
  );
}

interface BidLeaderboardProps {
  readonly bidHistory: BidHistoryEntry[];
  readonly formatCurrency: (amount: number) => string;
}

function BidLeaderboard({
  bidHistory,
  formatCurrency,
}: BidLeaderboardProps): React.JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Bid Leaderboard</span>
      </div>
      <div className="space-y-2">
        {bidHistory.slice(0, 5).map((bid) => (
          <BidRow
            key={`${bid.rank}-${bid.supplierName}`}
            bid={bid}
            formatCurrency={formatCurrency}
          />
        ))}
      </div>
    </div>
  );
}

interface BidRowProps {
  readonly bid: BidHistoryEntry;
  readonly formatCurrency: (amount: number) => string;
}

function BidRow({
  bid, formatCurrency 
}: BidRowProps): React.JSX.Element {
  const isLeader = bid.rank === 1;
  const rankStyle = isLeader
    ? 'bg-primary text-primary-foreground'
    : 'bg-muted text-muted-foreground';

  return (
    <div className={`flex items-center justify-between p-2 rounded text-sm ${bid.isYou ? 'bg-muted' : ''}`}>
      <div className="flex items-center gap-3">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${rankStyle}`}>
          {bid.rank}
        </span>
        <span className={bid.isYou ? 'font-medium' : ''}>{bid.supplierName}</span>
      </div>
      <div className="text-right">
        <p className="font-medium">{formatCurrency(bid.amount)}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(bid.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
